import {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {slices} from 'store';
import {
  getStorageTemplates,
  getWorkspaceTemplate,
  getWorkspaceTemplates,
} from 'utils/fast/metadata';
import {getTemplateIcon} from '../utils';

import type {TemplateItem} from 'fast-sdk/src/api/storage/consts';
import type {WorkspaceMetadataTemplate} from 'fast-sdk/src/api/workspace/consts';

export interface UseTemplatesProps {
  instanceId: string;
  instanceNs?: string;
  disabled?: boolean;
}

export function useTemplates({
  instanceId,
  instanceNs,
  disabled = false,
}: UseTemplatesProps) {
  const dispatch = useDispatch();

  // Select template data from Redux
  const templateState = useSelector(
    (state: any) =>
      state.metadata.byInstance?.[instanceId] || {
        templates: [],
        templateId: null,
        templateDb: {},
        isLoading: false,
      },
  );

  const {templates, templateId, templateDb, isLoading} = templateState;

  // Action dispatcher helpers
  const setTemplates = useCallback(
    (templatesList: TemplateItem[]) => {
      dispatch(
        slices.metadata.actions.setTemplates({
          instanceId,
          templates: templatesList,
        }),
      );
    },
    [dispatch, instanceId],
  );

  const setTemplateId = useCallback(
    (id: string | null) => {
      dispatch(
        slices.metadata.actions.setTemplateId({
          instanceId,
          templateId: id,
        }),
      );
    },
    [dispatch, instanceId],
  );

  const setTemplateDb = useCallback(
    (db: Record<string, WorkspaceMetadataTemplate>) => {
      dispatch(
        slices.metadata.actions.updateTemplateDb({
          instanceId,
          templates: db,
        }),
      );
    },
    [dispatch, instanceId],
  );

  const updateTemplateDb = useCallback(
    (templates: Record<string, WorkspaceMetadataTemplate>) => {
      dispatch(
        slices.metadata.actions.updateTemplateDb({
          instanceId,
          templates,
        }),
      );
    },
    [dispatch, instanceId],
  );

  const setIsLoading = useCallback(
    (loading: boolean) => {
      dispatch(
        slices.metadata.actions.setIsLoading({
          instanceId,
          isLoading: loading,
        }),
      );
    },
    [dispatch, instanceId],
  );

  const selectTemplate = useCallback(
    (id: string) => {
      setTemplateId(id);
    },
    [setTemplateId],
  );

  // Get template data with icon
  const getTemplateData = useCallback(
    (id: string) => {
      const lookup = templateDb[id];
      return {
        ...lookup,
        icon: getTemplateIcon(lookup?.category),
      };
    },
    [templateDb],
  );

  // Fetch template by ID and update the template DB
  const fetchTemplateById = useCallback(
    async (id: string) => {
      if (!instanceId) return null;

      const result = await getWorkspaceTemplate(instanceId, id);

      if (result.result && result.item) {
        // Update template DB with the new template
        updateTemplateDb({[result.item.id]: result.item});
        return result.item;
      }

      return null;
    },
    [instanceId, updateTemplateDb],
  );

  // Fetch multiple templates by IDs
  const fetchTemplatesByIds = useCallback(
    async (ids: string[]) => {
      if (!instanceId || !ids.length) return;

      // Filter out template IDs that are already in the database
      const idsToFetch = ids.filter(id => !templateDb[id]);

      if (idsToFetch.length === 0) {
        // All templates are already in the database, no need to fetch
        return;
      }

      const results = [];
      for (let i = 0; i < idsToFetch.length; i++) {
        const result = await getWorkspaceTemplate(instanceId, idsToFetch[i]);
        results.push(result);

        // Add 2-second delay between requests (except after the last one)
        if (i < idsToFetch.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      // Build new templates object
      const newTemplates = results.reduce(
        (acc, result) => {
          if (result.result && result.item) {
            acc[result.item.id] = result.item;
          }
          return acc;
        },
        {} as Record<string, WorkspaceMetadataTemplate>,
      );

      // Update template DB in Redux
      updateTemplateDb(newTemplates);
    },
    [instanceId, templateDb, updateTemplateDb],
  );

  // Fetch all workspace templates
  const fetchAllWorkspaceTemplates = useCallback(async () => {
    if (!instanceId || disabled) return [];
    const {result, items} = await getWorkspaceTemplates(instanceId);
    if (result && items.length > 0) {
      // Create template DB object
      const templatesObj = items.reduce(
        (acc, item) => {
          acc[item.id] = item;
          return acc;
        },
        {} as Record<string, WorkspaceMetadataTemplate>,
      );

      // Set template DB in Redux
      setTemplateDb(templatesObj);
      return items;
    }
  }, [instanceId, disabled, setTemplateDb]);

  // Fetch templates for a specific folder
  const fetchFolderTemplates = useCallback(async () => {
    if (!instanceId || !instanceNs || disabled) return [];
    setIsLoading(true);
    // Get templates available in the current folder
    const {result, items} = await getStorageTemplates(
      instanceId,
      instanceNs as any, // Type cast to resolve potential type mismatch
    );
    if (result && items.length > 0) {
      setTemplates(items);
      // Select template with highest object count
      const {template_id} = items.reduce((max, current) =>
        current.object_count > max.object_count ? current : max,
      );
      selectTemplate(template_id);
      // Fetch detailed template data
      await fetchTemplatesByIds(items.map(item => item.template_id));
    }
    setIsLoading(false);
  }, [
    instanceId,
    instanceNs,
    disabled,
    setTemplates,
    setTemplateId,
    setIsLoading,
    selectTemplate,
    fetchTemplatesByIds,
  ]);

  // Fetch all workspace template data when instance changes
  useEffect(() => {
    fetchAllWorkspaceTemplates();
  }, [instanceId, disabled, fetchAllWorkspaceTemplates]);

  // Fetch used templates when instance changes
  useEffect(() => {
    fetchFolderTemplates();
  }, [instanceId, instanceNs, disabled, fetchFolderTemplates]);

  return {
    // State
    templates,
    templateId,
    templateDb,
    isLoading,

    // Actions
    selectTemplate,
    getTemplateData,

    // Fetching functions
    fetchTemplateById,
  };
}
